/**
 * The pages the owner added, from the images of a session (plan
 * menu-import-fixes F5). A very tall page (a website saved as one long PDF
 * page, a long screenshot) is read in parts, each one an image of its own;
 * the parts of one page follow each other, numbered from 1, with the same
 * origin, file and PDF page. Pure.
 */
import type { TMenuImportPageOrigin } from '@/interfaces/IMenuImport';

/** What tells the parts of one very tall page apart from another page. */
export interface IPartIdentity {
  origin: TMenuImportPageOrigin;
  originName: string | null;
  originPage: number | null;
  /** 1-based part of a very tall page read in parts; null for a whole page. */
  originPart: number | null;
}

/** `next` is the part right after `previous` on the same very tall page. */
export function continuesPage(
  previous: IPartIdentity,
  next: IPartIdentity
): boolean {
  return (
    previous.originPart !== null &&
    next.originPart !== null &&
    next.originPart === previous.originPart + 1 &&
    next.origin === previous.origin &&
    next.originName === previous.originName &&
    next.originPage === previous.originPage
  );
}

/** Images in order, grouped by the page the owner added (a whole page is a group of one). */
export function groupOwnerPages<T extends IPartIdentity>(
  images: readonly T[]
): T[][] {
  const groups: T[][] = [];
  for (const image of images) {
    const group = groups.length > 0 ? groups[groups.length - 1] : null;
    if (group !== null && continuesPage(group[group.length - 1], image))
      group.push(image);
    else groups.push([image]);
  }
  return groups;
}
